import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, GlassCard, GradButton, Field } from "@/components/erp";
import { ListeYonetimi } from "@/components/ListeYonetimi";
import { ErtelemeAyar } from "@/components/ErtelemeAyar";
import { EDonusumAyar } from "@/components/EDonusumAyar";
import { FinekraAyar } from "@/components/FinekraAyar";
import { SelectBox } from "@/components/Inputs";
import { ONAY_KURAL } from "@/lib/perms";
import { KurAyar } from "@/components/KurAyar";
import { MarkaLogolari } from "@/components/MarkaLogolari";
import { SirketBilgileri } from "@/components/SirketBilgileri";
import { useAuth, applyTheme } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { BelgeNumaralari } from "@/components/satis/BelgeNumaralari";
import { DepartmanAyar } from "@/components/cari/DepartmanAyar";
import { SubeListesiAyar } from "@/components/finans/SubeListesiAyar";

const FIRMA = [["unvan", "Ticari Unvan"], ["kisa_ad", "Kısa Ad"], ["sirket_turu", "Şirket Türü"], ["vergi_dairesi", "Vergi Dairesi"], ["vergi_no", "VKN / TCKN"],
  ["mersis_no", "MERSİS Numarası"], ["ticaret_sicil_no", "Ticaret Sicil No"], ["telefon", "Telefon"], ["email", "E-posta"], ["web_sitesi", "İnternet Sitesi"],
  ["ulke", "Ülke"], ["sehir", "Şehir"], ["ilce", "İlçe"], ["adres", "Adres"], ["isletme_merkezi", "İşletme Merkezi"], ["nace", "NACE Kodu"], ["tehlike_sinifi", "Tehlike Sınıfı"],
  ["sgk_sicil", "SGK Sicil No"], ["isveren_vekili", "İşveren Vekili"], ["logo_url", "Logo URL"]];
const ONEK = [["satis", "Satış"], ["is_emri", "İş Emri"], ["sevkiyat", "Sevkiyat"], ["alis", "Alış"]];
const TEMALAR = { gece: ["Gece", "#3b82f6,#10b981,#8b5cf6"], grafit: ["Grafit", "#f97316,#eab308,#ef4444"], okyanus: ["Okyanus", "#06b6d4,#14b8a6,#6366f1"] };
const csv = (a) => (a || []).join(", ");
const fromCsv = (s) => String(s).split(",").map((x) => x.trim()).filter(Boolean);

export default function Ayarlar() {
  const { ayar, loadAyar } = useAuth();
  const [f, setF] = useState(null);
  const [tab, setTab] = useState("sirket");
  useEffect(() => {
    if (ayar?.firma_id) setF({ ...ayar, kategoriler: csv(ayar.kategoriler), birimler: csv(ayar.birimler), kdv_oranlari: csv(ayar.kdv_oranlari), ozet_eposta_listesi: csv(ayar.ozet_eposta_listesi) });
  }, [ayar]);
  if (!f) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setOnek = (k, v) => setF((s) => ({ ...s, belge_onekleri: { ...s.belge_onekleri, [k]: v } }));

  const kaydet = async () => {
    try {
      await api.put("/ayarlar", { ...f, kategoriler: fromCsv(f.kategoriler), birimler: fromCsv(f.birimler), kdv_oranlari: fromCsv(f.kdv_oranlari).map(Number), ozet_eposta_listesi: fromCsv(f.ozet_eposta_listesi) });
      await loadAyar();
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const inp = (k, type = "text") => <Input data-testid={`ayar-${k}`} type={type} className="bg-white/5" value={f[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} />;

  return (
    <div data-testid="ayarlar-page">
      <PageHeader title={tab === "sirket" ? "Şirket Ayarları" : "Ayarlar"}>
        {!["sirket", "edonusum", "finekra", "logolar", "banka"].includes(tab) && <GradButton data-testid="ayar-kaydet" onClick={kaydet}><Save className="w-4 h-4" />Kaydet</GradButton>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 flex-wrap h-auto">
          {[["sirket", "Şirket"], ["firma", "Firma Bilgileri"], ["belge", "Belge & Vergi"], ["departman", "Departmanlar"], ["banka", "Banka Şubeleri"], ["liste", "Kategori & Birim"], ["alan", "Ürün Alanları"], ["ambalaj", "Ambalaj"], ["logolar", "Marka Logoları"], ["bildirim", "Bildirimler"], ["erteleme", "Ödeme Erteleme"], ["edonusum", "e-Dönüşüm (QNB)"], ["finekra", "Finekra"], ["tema", "Tema"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="sirket">{tab === "sirket" && <SirketBilgileri onDuzenle={(t) => setTab(t === "adres" ? "firma" : t)} />}</TabsContent>
        <TabsContent value="firma"><GlassCard className="p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{FIRMA.map(([k, l]) => <Field key={k} label={l}>{inp(k)}</Field>)}</GlassCard></TabsContent>
        <TabsContent value="belge">
          <div className="mb-4"><BelgeNumaralari /></div>
          <GlassCard className="p-6 space-y-6">
            <div className="grid sm:grid-cols-5 gap-4">{ONEK.map(([k, l]) => (
              <Field key={k} label={`${l} öneki`}><Input data-testid={`onek-${k}`} className="bg-white/5 font-mono" value={f.belge_onekleri?.[k] ?? ""} onChange={(e) => setOnek(k, e.target.value)} /></Field>
            ))}</div>
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="KDV oranları (virgülle)">{inp("kdv_oranlari")}</Field>
              <Field label="Varsayılan KDV %">{inp("varsayilan_kdv", "number")}</Field>
              <Field label="Minimum kâr % (fiyat tabanı)">{inp("min_kar_yuzde", "number")}</Field>
              <Field label="Varsayılan termin (gün)">{inp("termin_gun", "number")}</Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Yeni müşterilerde sipariş onay kuralı (cari kartından değiştirilebilir)">
                <SelectBox testid="ayar-onay-kurali" value={f.varsayilan_onay_kurali || "siparis_dahil"} onChange={(v) => set("varsayilan_onay_kurali", v)}
                  options={Object.entries(ONAY_KURAL).map(([value, label]) => ({ value, label }))} />
              </Field>
            </div>
            <label className="flex items-center gap-3 text-sm"><Switch data-testid="ayar-lot-skt" checked={!!f.lot_skt_zorunlu} onCheckedChange={(v) => set("lot_skt_zorunlu", v)} />Lot / SKT takibi zorunlu (gıda)</label>
            <KurAyar f={f} set={set} setF={setF} />
          </GlassCard>
        </TabsContent>
        <TabsContent value="liste"><GlassCard className="p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Ürün kategorileri (virgülle)">{inp("kategoriler")}</Field>
          <Field label="Ölçü birimleri (virgülle)">{inp("birimler")}</Field>
        </GlassCard></TabsContent>
        <TabsContent value="alan"><ListeYonetimi path="oznitelikler" /></TabsContent>
        <TabsContent value="ambalaj"><ListeYonetimi path="ambalajlar" /></TabsContent>
        <TabsContent value="bildirim"><GlassCard className="p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Verilen çek uyarısı: vadeden kaç gün önce (0–90)"><Input data-testid="ayar-cek_uyari_gun" type="number" min="0" max="90" className="bg-white/5" value={f.cek_uyari_gun ?? 7} onChange={(e) => set("cek_uyari_gun", Number(e.target.value))} /></Field>
          <Field label="Minimum nakit bakiye (TL, 0 = sadece sıfırın altı)"><Input data-testid="ayar-min_nakit_bakiye" type="number" min="0" step="any" className="bg-white/5" value={f.min_nakit_bakiye ?? 0} onChange={(e) => set("min_nakit_bakiye", Number(e.target.value))} /></Field>
          <p className="sm:col-span-2 text-xs text-muted-foreground">Günlük özet her sabah 09:00'da muhasebe, yönetici ve admin ekranında görünür. E-posta alıcıları ve kişisel e-posta tercihleri Ayarlar &gt; E-Posta Ayarları bölümündedir.</p>
        </GlassCard></TabsContent>
        <TabsContent value="erteleme"><ErtelemeAyar value={f.erteleme} onChange={(v) => set("erteleme", v)} /></TabsContent>
        <TabsContent value="logolar">{tab === "logolar" && <MarkaLogolari />}</TabsContent>
        <TabsContent value="edonusum"><EDonusumAyar /></TabsContent>
        <TabsContent value="finekra"><FinekraAyar /></TabsContent>
        <TabsContent value="departman"><DepartmanAyar f={f} set={set} /></TabsContent>
        <TabsContent value="banka">{tab === "banka" && <SubeListesiAyar />}</TabsContent>
        <TabsContent value="tema">
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(TEMALAR).map(([k, [ad, renk]]) => (
              <button key={k} data-testid={`ayar-tema-${k}`} onClick={() => { set("tema", k); applyTheme(k); }}
                className={`glass rounded-2xl p-5 text-left transition-transform hover:-translate-y-1 ${f.tema === k ? "grad-border glow" : ""}`}>
                <div className="h-16 rounded-xl" style={{ background: `linear-gradient(120deg, ${renk})` }} />
                <div className="mt-3 text-sm font-medium">{ad}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Tema önizlemesi anında uygulanır; kalıcı olması için Kaydet'e basın.</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
